/**
 * <s-review-marquee> — Review cards that glide from right to left on their own and
 * can also be swiped or scrolled by hand.
 *
 * Required child markup:
 *   [data-track] — Horizontally scrolling element holding the cards twice in a row
 *                  (the second copy lets the loop wrap seamlessly).
 *   [data-set]   — Each copy of the card list; the first one sets the loop length.
 *
 * Element dataset:
 *   data-speed — Glide speed in px per second (default 40).
 *
 * Gliding pauses while a pointer is over the row, while it has focus, and for a few
 * seconds after a touch. It never runs for visitors who prefer reduced motion or
 * while the row is off screen.
 */
class SReviewMarquee extends HTMLElement {
  #ac;
  #track;
  #set;
  #frame = 0;
  #last = 0;
  #position = 0;
  #paused = false;
  #visible = false;
  #resumeTimer;
  #observer;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    this.#set = this.querySelector('[data-set]');
    if (!this.#track || !this.#set) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (getComputedStyle(this.#track).direction === 'rtl') return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const pause = () => { clearTimeout(this.#resumeTimer); this.#paused = true; };
    const resume = (delay = 0) => {
      clearTimeout(this.#resumeTimer);
      this.#resumeTimer = setTimeout(() => {
        this.#paused = false;
        this.#position = this.#track.scrollLeft;
      }, delay);
    };

    this.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') pause(); }, { signal });
    this.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') resume(); }, { signal });
    this.addEventListener('touchstart', pause, { passive: true, signal });
    this.addEventListener('touchend', () => resume(2500), { passive: true, signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', () => resume(), { signal });
    this.#track.addEventListener('scroll', () => {
      if (this.#paused) this.#wrap();
    }, { passive: true, signal });

    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      if (this.#visible) this.#start();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#resumeTimer);
  }

  #loopWidth() {
    const gap = parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    return this.#set.getBoundingClientRect().width + gap;
  }

  #wrap() {
    const loop = this.#loopWidth();
    if (loop <= 0) return;
    if (this.#track.scrollLeft >= loop) this.#track.scrollLeft -= loop;
    else if (this.#track.scrollLeft <= 0) this.#track.scrollLeft += loop;
  }

  #start() {
    cancelAnimationFrame(this.#frame);
    this.#last = performance.now();
    this.#position = this.#track.scrollLeft;
    const speed = Number(this.dataset.speed) || 40;
    const step = (now) => {
      if (!this.#visible) return;
      const elapsed = Math.min(now - this.#last, 64);
      this.#last = now;
      if (!this.#paused) {
        const loop = this.#loopWidth();
        this.#position += (speed * elapsed) / 1000;
        if (loop > 0 && this.#position >= loop) this.#position -= loop;
        this.#track.scrollLeft = this.#position;
      }
      this.#frame = requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('s-review-marquee')) {
  customElements.define('s-review-marquee', SReviewMarquee);
}
